import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { API, useAuth } from '../App';
import { useT } from '../i18n';
import {
  fetchInstanceLicence,
  invalidateInstanceLicence,
  peekInstanceLicence,
} from '../lib/instanceLicence';
import {
  LICENCE_ASK_ADMIN,
  LICENCE_SETUP_INTRO,
  LICENCE_STOPPED,
  LICENCE_TEST_BANNER,
  LICENCE_WARNING,
} from '../i18n/instanceLicence';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { AlertTriangle, Info, Key } from 'lucide-react';

/*
  Self-hosted installs only. Without a valid lease the workspace shows the
  activation screen (platform admins enter the key; everyone else is asked to
  contact them). With a lease it shows a banner when renewal is failing and on
  test servers. On our cloud it renders the children untouched.
*/

function LicenceSetup({ status, isAdmin, onActivated }) {
  const { t } = useT();
  const [code, setCode] = useState('');
  const [role, setRole] = useState('production');
  const [busy, setBusy] = useState(false);
  const stopped = status.state !== 'unlicensed';

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      await axios.post(`${API}/instance/licence/activate`, { licence_key: code.trim(), role });
      invalidateInstanceLicence();
      const fresh = await fetchInstanceLicence(API, { fresh: true });
      toast.success(t('Licence activated'));
      onActivated(fresh);
    } catch (err) {
      toast.error(err.response?.data?.detail || t('Action failed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-[70vh] flex items-center justify-center p-6" data-testid="licence-setup">
      <Card className="w-full max-w-lg">
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-base">
            <Key className="w-4 h-4" /> {stopped ? t('Licence expired') : t('Activate this installation')}
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-4 text-sm">
          <p className="text-muted-foreground">{t(stopped ? LICENCE_STOPPED : LICENCE_SETUP_INTRO)}</p>
          {isAdmin ? (
            <form onSubmit={submit} className="space-y-3">
              <Input
                value={code}
                onChange={(e) => setCode(e.target.value)}
                placeholder="XPNSR-XXXXX-XXXXX-XXXXX-XXXXX-XXXXX-XXXXX"
                className="font-mono"
                autoComplete="off"
                spellCheck={false}
                data-testid="licence-input"
              />
              <div className="flex gap-2">
                {['production', 'test'].map((r) => (
                  <button
                    key={r}
                    type="button"
                    onClick={() => setRole(r)}
                    className={
                      'rounded-lg border px-3 py-1.5 text-xs transition-colors ' +
                      (role === r
                        ? 'border-primary bg-primary/10 font-medium text-primary'
                        : 'border-border text-muted-foreground hover:border-primary/40 hover:text-foreground')
                    }
                    data-testid={`licence-role-${r}`}
                  >
                    {r === 'production' ? t('Production') : t('Test')}
                  </button>
                ))}
              </div>
              <Button type="submit" className="w-full" disabled={busy || !code.trim()} data-testid="licence-activate">
                {t('Activate')}
              </Button>
            </form>
          ) : (
            <p>{t(LICENCE_ASK_ADMIN)}</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

export default function LicenceGate({ children }) {
  const { t } = useT();
  const { user } = useAuth();
  const [status, setStatus] = useState(() => peekInstanceLicence());

  useEffect(() => {
    let alive = true;
    fetchInstanceLicence(API).then((s) => {
      if (alive) setStatus(s);
    });
    return () => {
      alive = false;
    };
  }, []);

  if (!status || status.mode !== 'self_hosted') return children;
  if (status.state !== 'active') {
    return <LicenceSetup status={status} isAdmin={Boolean(user && user.is_admin)} onActivated={setStatus} />;
  }
  const until = status.expires_at ? new Date(status.expires_at).toLocaleDateString() : '';
  return (
    <>
      {status.warning ? (
        <div className="mx-4 mt-4 flex items-center gap-2 rounded-lg border border-amber-500/40 bg-amber-500/10 px-4 py-2 text-sm" data-testid="licence-warning">
          <AlertTriangle className="w-4 h-4 text-amber-500 shrink-0" />
          {t(LICENCE_WARNING, { date: until })}
        </div>
      ) : null}
      {status.role === 'test' ? (
        <div className="mx-4 mt-4 flex items-center gap-2 rounded-lg border border-primary/30 bg-primary/5 px-4 py-2 text-sm" data-testid="licence-test-banner">
          <Info className="w-4 h-4 text-primary shrink-0" />
          {t(LICENCE_TEST_BANNER, { n: Number(status.daily_clicks || 0).toLocaleString() })}
        </div>
      ) : null}
      {children}
      {status.licensed_to || status.licence_id ? (
        <div className="px-4 pb-4 pt-8 text-center text-[11px] text-muted-foreground" data-testid="licence-watermark">
          {t('Licensed to {who} · licence {id}', { who: status.licensed_to || '—', id: status.licence_id || '—' })}
        </div>
      ) : null}
    </>
  );
}
